import {useCallback} from 'react';
import {
  css,
  view,
  Icon,
  Action,
  Tooltip,
  Menu,
  MenuItem,
  Href,
  tokens,
  createView,
} from '@sail/ui';
import {
  cancel,
  overflow,
  support,
  panelArrowRight,
  chevronDown,
  chevronUp,
} from '@sail/icons/react/Icon';
import {defineMessages, FormattedMessage} from 'react-intl';
import {useUniversalHelpDrawerEnabled} from '../FeatureFlagsProvider';
import {widgetLinkAction} from '../helpers/widgetLinkAction';
import {ActiveTopBar} from '../Widget';
import {useAnalytics} from '../AnalyticsProvider';
import {DevBadge} from './DevBadge';

const messages = defineMessages({
  developerDocs: {
    id: 'conversations_ui.header.developerDocs',
    description: 'Label for the developer docs link',
    defaultMessage: 'Developer docs',
  },
  supportArticles: {
    id: 'conversations_ui.header.supportArticles',
    description: 'Label for the support articles link',
    defaultMessage: 'Support articles',
  },
  supportCenter: {
    id: 'conversations_ui.header.supportCenter',
    description: 'Label for the support center link',
    defaultMessage: 'Support center',
  },
  restartConversation: {
    id: 'conversations_ui.header.restartConversation',
    description: 'Label for restart conversation link',
    defaultMessage: 'Restart conversation',
  },
  minimizeTooltip: {
    id: 'conversations_ui.header.minimizeTooltip',
    description: 'Label for the minimize botton',
    defaultMessage: 'Minimize',
  },
  closeTooltip: {
    id: 'conversations_ui.header.closeTooltip',
    description: 'Label for the close botton',
    defaultMessage: 'Close',
  },
  moreTooltip: {
    id: 'conversations_ui.header.moreTooltip',
    description: 'Label for the more botton',
    defaultMessage: 'More',
  },
});

const buttonStyles = css({
  display: 'inline-flex',
  borderRadius: 'rounded',
  padding: 'small',
  ':hover': {
    backgroundColor: 'offset',
  },
});

function getMinimizeIcon(drawerEnabled: boolean, minimized?: boolean) {
  if (drawerEnabled) {
    return panelArrowRight;
  }

  return minimized ? chevronUp : chevronDown;
}

type Props = {
  onClose: () => void;
  onRestart: () => void;
  onMinimize?: () => void;
  onOpen?: () => void;
  entryPoint?: string;
  minimized?: boolean;
  supportCenterHref?: Href;
};

export const StripeAssistantHeader = createView(
  ({
    minimized,
    supportCenterHref,
    entryPoint,
    onMinimize,
    onOpen,
    onClose,
    onRestart,
    ...props
  }: Props) => {
    const analytics = useAnalytics();
    const universalHelpDrawerEnabled = useUniversalHelpDrawerEnabled();

    const isSupportSite = entryPoint === 'support_site';
    const isExpressSupport = entryPoint === 'express_support_site';
    const isOnboarding = entryPoint === 'onboarding';
    const isDashboard = entryPoint === 'dashboard';
    const shouldCloseIconTriggerMinimize = isDashboard || isOnboarding;

    const handleClose = useCallback(() => {
      if (shouldCloseIconTriggerMinimize) {
        onMinimize?.();
      } else {
        onClose();
      }
      analytics.action('stripe_assistant_header_close_click');
    }, [shouldCloseIconTriggerMinimize, onMinimize, analytics, onClose]);

    return (
      <view.header data-testid="assistant-header" {...props}>
        <view.div
          css={{
            stack: 'x',
            paddingX: 'medium',
            paddingY: 'small',
            borderBottom: '1px solid',
            borderBottomColor: 'border',
            alignY: 'center',
            distribute: 'space-between',
            zIndex: 2,
            height: isOnboarding ? 'space.800' : 52,
            background: `color-mix(in rgb, ${tokens.color.background.surface} 25%, transparent 75%)`,
            backdropFilter: 'blur(4px)',
          }}
        >
          <view.h1
            css={{
              font: 'heading.small',
              margin: 'space.0',
              stack: 'x',
              gap: 'small',
              alignY: 'center',
            }}
          >
            <Sparkle />
            Assistant
            <DevBadge />
          </view.h1>

          <view.div css={{bleedRight: 'small', gap: 'xsmall'}}>
            {!isSupportSite && !isExpressSupport ? (
              <Menu
                trigger={
                  <Tooltip
                    type="label"
                    trigger={
                      <Action css={{padding: 'small'}} uses={[buttonStyles]}>
                        <Icon
                          icon={overflow}
                          size="xsmall"
                          data-testid="assistant-header-more-menu"
                        />
                      </Action>
                    }
                  >
                    <FormattedMessage {...messages.moreTooltip} />
                  </Tooltip>
                }
              >
                <MenuItem
                  href="https://docs.stripe.com/"
                  target="stripe-docs"
                  data-testid="assistant-header-stripe-docs-link"
                  onAction={() => {
                    analytics.action(
                      'stripe_assistant_header_stripe_docs_click',
                    );
                  }}
                >
                  <FormattedMessage {...messages.developerDocs} />
                </MenuItem>
                <MenuItem
                  href="https://support.stripe.com/"
                  target="stripe-articles"
                  data-testid="assistant-header-stripe-articles-link"
                  onAction={() => {
                    analytics.action(
                      'stripe_assistant_header_stripe_articles_click',
                    );
                  }}
                >
                  <FormattedMessage {...messages.supportArticles} />
                </MenuItem>
                <MenuItem
                  type="destructive"
                  data-testid="assistant-header-stripe-restart-conversation"
                  onAction={() => {
                    onRestart();
                    analytics.action(
                      'stripe_assistant_header_stripe_restart_conversation',
                    );
                  }}
                >
                  <FormattedMessage {...messages.restartConversation} />
                </MenuItem>
              </Menu>
            ) : null}

            {supportCenterHref ? (
              <Tooltip
                type="label"
                trigger={
                  <Action
                    href={supportCenterHref}
                    uses={[widgetLinkAction(), buttonStyles]}
                    onClick={() => {
                      analytics.action(
                        'stripe_assistant_header_support_center_click',
                      );
                    }}
                  >
                    <Icon
                      icon={support}
                      size="xsmall"
                      data-testid="assistant-header-support-center-link"
                    />
                  </Action>
                }
              >
                <FormattedMessage {...messages.supportCenter} />
              </Tooltip>
            ) : null}
            {!shouldCloseIconTriggerMinimize ? (
              <Tooltip
                type="label"
                delay={600}
                trigger={
                  <Action
                    css={{padding: 'small'}}
                    onPress={() => {
                      if (minimized) {
                        onOpen?.();
                      } else {
                        onMinimize?.();
                      }
                      analytics.action(
                        'stripe_assistant_header_minimize_click',
                      );
                    }}
                    uses={[buttonStyles]}
                  >
                    <Icon
                      data-testid="assistant-header-minimize-button"
                      icon={getMinimizeIcon(
                        universalHelpDrawerEnabled,
                        minimized,
                      )}
                      size="xsmall"
                    />
                  </Action>
                }
              >
                <FormattedMessage {...messages.minimizeTooltip} />
              </Tooltip>
            ) : null}

            <Tooltip
              type="label"
              delay={600}
              trigger={
                <Action
                  data-testid="conversations-widget-header-close-button"
                  onPress={() => handleClose()}
                  uses={[buttonStyles]}
                >
                  <Icon icon={cancel} size="xsmall" />
                </Action>
              }
            >
              <FormattedMessage {...messages.closeTooltip} />
            </Tooltip>
          </view.div>
        </view.div>
        <ActiveTopBar />
      </view.header>
    );
  },
);

function Sparkle() {
  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.4715 3.12411C11.5425 3.09893 11.5984 3.04302 11.6236 2.97196L12.2639 1.16504C12.3032 1.05404 12.4014 0.99854 12.4995 0.998535C12.5977 0.998531 12.6958 1.05403 12.7352 1.16504L13.3754 2.97196C13.4006 3.04302 13.4565 3.09893 13.5276 3.12411L15.3345 3.76436C15.4455 3.80369 15.501 3.90184 15.501 4C15.501 4.09816 15.4455 4.19631 15.3345 4.23565L13.5276 4.8759C13.4565 4.90108 13.4006 4.95698 13.3754 5.02805L12.7352 6.83497C12.6958 6.94597 12.5977 7.00147 12.4995 7.00147C12.4014 7.00147 12.3032 6.94596 12.2639 6.83497L11.6236 5.02805C11.5984 4.95698 11.5425 4.90108 11.4715 4.8759L9.66456 4.23565C9.55356 4.19632 9.49805 4.09816 9.49806 4C9.49806 3.90184 9.55356 3.80369 9.66456 3.76436L11.4715 3.12411Z"
        fill="url(#paint0_linear_757_90506)"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.99952 5.99119L6.43977 7.57092C6.21315 8.21049 5.71001 8.71363 5.07044 8.94025L3.49071 9.5L5.07044 10.0598C5.71001 10.2864 6.21315 10.7895 6.43977 11.4291L6.99952 13.0088L7.55927 11.4291C7.78589 10.7895 8.28903 10.2864 8.9286 10.0598L10.5083 9.5L8.9286 8.94025C8.28903 8.71363 7.78589 8.21049 7.55927 7.57092L6.99952 5.99119ZM7.70645 3.49511C7.58846 3.1621 7.29398 2.99559 6.99951 2.9956C6.70505 2.9956 6.41058 3.1621 6.29259 3.49511L5.0259 7.06994C4.95036 7.28313 4.78265 7.45084 4.56946 7.52638L0.994626 8.79307C0.66162 8.91106 0.495117 9.20553 0.495117 9.5C0.495118 9.79447 0.661621 10.0889 0.994628 10.2069L4.56946 11.4736C4.78265 11.5492 4.95036 11.7169 5.0259 11.9301L6.29259 15.5049C6.41058 15.8379 6.70505 16.0044 6.99951 16.0044C7.29398 16.0044 7.58846 15.8379 7.70645 15.5049L8.97314 11.9301C9.04868 11.7169 9.21639 11.5492 9.42958 11.4736L13.0044 10.2069C13.3374 10.0889 13.5039 9.79447 13.5039 9.5C13.5039 9.20553 13.3374 8.91106 13.0044 8.79307L9.42958 7.52638C9.21639 7.45084 9.04868 7.28313 8.97314 7.06994L7.70645 3.49511Z"
        fill="url(#paint1_linear_757_90506)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_757_90506"
          x1="-2.24035"
          y1="19.38"
          x2="20.1597"
          y2="-3.02"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#55DDFF" />
          <stop offset="0.5" stopColor="#675DFF" />
          <stop offset="0.98" stopColor="#28239B" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_757_90506"
          x1="-2.24035"
          y1="19.38"
          x2="20.1597"
          y2="-3.02"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#55DDFF" />
          <stop offset="0.5" stopColor="#675DFF" />
          <stop offset="0.98" stopColor="#28239B" />
        </linearGradient>
      </defs>
    </svg>
  );
}
